import { FormErrorSummary } from "../core/form-error-summary";
import { queryHtmlElement } from "../core/dom";

/**
 * Enhances the landing-page login form with client-side validation and focus
 * management for server-rendered auth failures.
 */
export class LandingLoginController {
  private readonly errorSummary: FormErrorSummary;
  private readonly form: HTMLFormElement | null;
  private readonly emailInput: HTMLInputElement | null;
  private readonly notice: HTMLElement | null;
  private readonly passwordInput: HTMLInputElement | null;

  public constructor(root: HTMLElement) {
    this.errorSummary = new FormErrorSummary(root);
    this.form = queryHtmlElement(root, "[data-login-form]", HTMLFormElement);
    this.emailInput = queryHtmlElement(root, "[data-login-email]", HTMLInputElement);
    this.notice = queryHtmlElement(root, "[data-login-notice]", HTMLElement);
    this.passwordInput = queryHtmlElement(root, "[data-login-password]", HTMLInputElement);
  }

  public connect(): void {
    if (this.form !== null) {
      this.form.addEventListener("submit", this.handleSubmit);
    }

    if (this.errorSummary.hasVisibleContent()) {
      this.errorSummary.focus();
    } else if (this.notice !== null) {
      this.notice.focus();
    }
  }

  private readonly handleSubmit = (event: SubmitEvent): void => {
    this.errorSummary.clear();
    this.form?.classList.add("was-validated");
    this.emailInput?.removeAttribute("aria-invalid");
    this.passwordInput?.removeAttribute("aria-invalid");

    if (this.emailInput === null) {
      return;
    }

    this.emailInput.value = this.emailInput.value.trim();
    if (this.emailInput.value.length === 0) {
      this.showValidationFailure(
        event,
        "Enter the email address for your WISP Planner account.",
        this.emailInput
      );
      return;
    }

    if (!this.emailInput.checkValidity()) {
      this.showValidationFailure(
        event,
        "Enter a valid email address before signing in.",
        this.emailInput
      );
      return;
    }

    if (this.passwordInput !== null && this.passwordInput.value.length === 0) {
      this.showValidationFailure(event, "Enter your password before signing in.", this.passwordInput);
    }
  };

  private showValidationFailure(event: SubmitEvent, message: string, input: HTMLInputElement): void {
    event.preventDefault();
    input.setAttribute("aria-invalid", "true");
    this.errorSummary.show([{ href: `#${input.id}`, message }]);
    this.errorSummary.focus();
  }
}
